import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { CheckIcon, ChevronRightIcon, DotFilledIcon } from "@radix-ui/react-icons";

import { cn } from "@/lib/utils";

const SqDropdownMenu = DropdownMenuPrimitive.Root;

interface SqDropdownMenuTriggerProps extends React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Trigger
> {
  bgColor?: string;
  outlineColor?: string;
  hoverBgColor?: string;
  hoverOutlineColor?: string;
  radius?: number;
  shadow?: "none" | "sm" | "md" | "lg" | "xl";
  hasOutline?: boolean;
}

const SqDropdownMenuTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Trigger>,
  SqDropdownMenuTriggerProps
>(
  (
    {
      className,
      children,
      shadow,
      bgColor,
      outlineColor,
      hoverBgColor,
      hoverOutlineColor,
      radius = 8,
      hasOutline = true,
      style,
      ...props
    },
    ref,
  ) => {
    // Map custom colors into CSS variables, then bind those to squircle painter vars via
    // Tailwind arbitrary properties (with !important to override variant defaults).
    const customCssVars = {
      ...(style || {}),
      "--sq-radius": radius ? `${radius}px` : "8px",
      ...(bgColor && { "--sq-fill": bgColor }),
      ...(hoverBgColor && { "--sq-hover-fill": hoverBgColor }),
      ...(outlineColor && { "--sq-outline": outlineColor }),
      ...(hoverOutlineColor && { "--sq-hover-outline": hoverOutlineColor }),
    } as React.CSSProperties;

    const colorBindingClasses = cn(
      bgColor && "squircle-fill-[var(--sq-fill)]",
      hoverBgColor && "hover:squircle-fill-[var(--sq-hover-fill)]",
      hasOutline && "before:squircle-outline-[1px]",
      hasOutline && outlineColor && "before:squircle-outline-color-[var(--sq-outline)]",
      hasOutline && !outlineColor && "before:squircle-outline-color-[#E7E8E9]",
      hasOutline &&
        hoverOutlineColor &&
        "hover:before:squircle-outline-color-[var(--sq-hover-outline)]",
    );

    const radiusClasses = cn(
      "squircle-radius-[var(--sq-radius)]",
      "before:squircle-radius-[var(--sq-radius)]",
    );

    const shadowClasses = cn(
      shadow === "sm" && "drop-shadow-sm",
      shadow === "md" && "drop-shadow-md",
      shadow === "lg" && "drop-shadow-lg",
      shadow === "xl" && "drop-shadow-xl",
    );

    const outlineClasses = cn(
      hasOutline &&
        "relative before:absolute before:inset-0 before:squircle before:squircle-radius-[var(--sq-radius)] before:squircle-outline-[1px] before:squircle-outline-color-[#E7E8E9] before:pointer-events-none",
    );

    return (
      <DropdownMenuPrimitive.Trigger
        ref={ref}
        className={cn(
          "transition-all",
          shadowClasses,
          outlineClasses,
          colorBindingClasses,
          radiusClasses,
          "shadow-none bg-transparent flex h-8 items-center justify-between whitespace-nowrap px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 outline-none drop-shadow-sm",
          !bgColor &&
            "squircle squircle-radius-[8px] squircle-smooth-[1] squircle-fill-[white]",
          !hoverBgColor && "hover:squircle-fill-hover-gray",
          className,
        )}
        style={customCssVars}
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.Trigger>
    );
  },
);
SqDropdownMenuTrigger.displayName = DropdownMenuPrimitive.Trigger.displayName;

const SqDropdownMenuGroup = DropdownMenuPrimitive.Group;

const SqDropdownMenuPortal = DropdownMenuPrimitive.Portal;

const SqDropdownMenuSub = DropdownMenuPrimitive.Sub;

const SqDropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const SqDropdownItemIndicator = DropdownMenuPrimitive.ItemIndicator;

const SqDropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
    hideChevron?: boolean;
  }
>(({ className, inset, children, hideChevron, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      "flex cursor-pointer select-none items-center squircle squircle-radius-[6px] px-2 py-1.5 text-sm outline-none",
      "hover:squircle-fill-hover-gray data-[state=open]:squircle-fill-hover-gray",
      inset && "pl-8",
      className,
    )}
    {...props}
  >
    {children}
    {!hideChevron && <ChevronRightIcon className="ml-auto h-4 w-4" />}
  </DropdownMenuPrimitive.SubTrigger>
));
SqDropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;

const SqDropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(
      "z-50 min-w-[8rem] bg-transparent overflow-hidden squircle squircle-radius-[8px] squircle-fill-[white] p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className,
    )}
    {...props}
  />
));
SqDropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;

interface SqDropdownMenuContentProps extends React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Content
> {
  sideOffset?: number;
  bgColor?: string;
  outlineColor?: string;
  radius?: number;
  shadow?: "none" | "sm" | "md" | "lg" | "xl";
  hasOutline?: boolean;
  portal?: boolean;
}

const SqDropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  SqDropdownMenuContentProps
>(
  (
    {
      className,
      sideOffset = 4,
      shadow,
      bgColor,
      outlineColor,
      radius = 8,
      hasOutline = true,
      portal = false,
      style,
      ...props
    },
    ref,
  ) => {
    const customCssVars = {
      ...(style || {}),
      ...(bgColor ? { "--sq-fill": bgColor } : {}),
      ...(outlineColor ? { "--sq-outline": outlineColor } : {}),
      "--sq-radius": `${radius}px`,
    } as React.CSSProperties & Record<string, string>;

    const colorBindingClasses = cn(
      bgColor && "squircle-fill-[var(--sq-fill)]",
      outlineColor && "before:squircle-outline-color-[var(--sq-outline)]",
    );

    const radiusClasses = cn(
      "squircle-radius-[var(--sq-radius)]",
      "before:squircle-radius-[var(--sq-radius)]",
    );

    const shadowClasses = cn(
      shadow === "sm" && "drop-shadow-sm",
      shadow === "md" && "drop-shadow-md",
      shadow === "lg" && "drop-shadow-lg",
      shadow === "xl" && "drop-shadow-xl",
    );

    const outlineClasses = cn(
      hasOutline &&
        "relative before:absolute before:inset-0 before:squircle before:squircle-outline-[1px] before:squircle-outline-color-border-gray before:pointer-events-none",
    );

    const content = (
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "squircle squircle-smooth-[1] squircle-fill-[white] transition-all",
          shadowClasses,
          outlineClasses,
          colorBindingClasses,
          radiusClasses,
          "z-50 min-w-[8rem] overflow-hidden p-1 text-popover-foreground drop-shadow-md",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className,
        )}
        style={customCssVars}
        {...props}
      />
    );

    if (portal) {
      return <DropdownMenuPrimitive.Portal>{content}</DropdownMenuPrimitive.Portal>;
    }

    return content;
  },
);
SqDropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

interface SqDropdownMenuItemProps extends React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Item
> {
  inset?: boolean;
  bgColor?: string;
  hoverBgColor?: string;
  radius?: number;
  isSelected?: boolean;
}

const SqDropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  SqDropdownMenuItemProps
>(
  (
    { className, inset, bgColor, hoverBgColor, radius = 6, style, isSelected, ...props },
    ref,
  ) => {
    const customCssVars = {
      ...(style || {}),
      ...(bgColor ? { "--sq-fill": bgColor } : {}),
      ...(hoverBgColor ? { "--sq-hover-fill": hoverBgColor } : {}),
      "--sq-radius": `${radius}px`,
    } as React.CSSProperties & Record<string, string>;

    const colorBindingClasses = cn(
      bgColor && "squircle-fill-[var(--sq-fill)]",
      hoverBgColor &&
        "hover:squircle-fill-[var(--sq-hover-fill)] focus:squircle-fill-[var(--sq-hover-fill)]",
    );

    const radiusClasses = cn("squircle-radius-[var(--sq-radius)]");

    return (
      <DropdownMenuPrimitive.Item
        ref={ref}
        className={cn(
          "h-7 relative flex select-none items-center squircle squircle-radius-[6px] px-2 py-1.5 text-[12px] outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer",
          !hoverBgColor && "hover:squircle-fill-hover-gray focus:squircle-fill-hover-gray",
          colorBindingClasses,
          inset && "pl-8",
          radiusClasses,
          className,
          isSelected && "!squircle-fill-selected-gray",
        )}
        style={customCssVars}
        onPointerLeave={(event) => event.preventDefault()}
        onPointerMove={(event) => event.preventDefault()}
        {...props}
      />
    );
  },
);
SqDropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

interface SqDropdownMenuCheckboxItemProps extends React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.CheckboxItem
> {
  bgColor?: string;
  hoverBgColor?: string;
  radius?: number;
}

const SqDropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  SqDropdownMenuCheckboxItemProps
>(
  (
    { className, children, checked, bgColor, hoverBgColor, radius = 6, style, ...props },
    ref,
  ) => {
    const customCssVars = {
      ...(style || {}),
      ...(bgColor ? { "--sq-fill": bgColor } : {}),
      ...(hoverBgColor ? { "--sq-hover-fill": hoverBgColor } : {}),
      "--sq-radius": `${radius}px`,
    } as React.CSSProperties & Record<string, string>;

    const colorBindingClasses = cn(
      bgColor && "squircle-fill-[var(--sq-fill)]",
      hoverBgColor &&
        "hover:squircle-fill-[var(--sq-hover-fill)] focus:squircle-fill-[var(--sq-hover-fill)]",
    );

    const radiusClasses = cn("squircle-radius-[var(--sq-radius)]");

    return (
      <DropdownMenuPrimitive.CheckboxItem
        ref={ref}
        className={cn(
          "relative flex cursor-default select-none items-center squircle squircle-radius-[6px] py-1.5 pl-8 pr-2 text-sm outline-none transition-colors hover:squircle-fill-hover-gray focus:squircle-fill-hover-gray data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          colorBindingClasses,
          radiusClasses,
          className,
        )}
        style={customCssVars}
        checked={checked}
        onPointerLeave={(event) => event.preventDefault()}
        onPointerMove={(event) => event.preventDefault()}
        {...props}
      >
        <span
          data-state={checked ? "checked" : "unchecked"}
          className="absolute left-2 flex h-4 w-4 items-center justify-center data-[state=unchecked]:bg-white data-[state=unchecked]:border data-[state=unchecked]:border-brand-primary data-[state=unchecked]:rounded-sm"
        >
          <DropdownMenuPrimitive.ItemIndicator className="data-[state=checked]:bg-brand-primary data-[state=unchecked]:border data-[state=checked]:text-white data-[state=checked]:rounded-sm">
            <CheckIcon className="h-4 w-4" />
          </DropdownMenuPrimitive.ItemIndicator>
        </span>
        {children}
      </DropdownMenuPrimitive.CheckboxItem>
    );
  },
);
SqDropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;

interface SqDropdownMenuRadioItemProps extends React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.RadioItem
> {
  bgColor?: string;
  hoverBgColor?: string;
  radius?: number;
}

const SqDropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  SqDropdownMenuRadioItemProps
>(({ className, children, bgColor, hoverBgColor, radius = 6, style, ...props }, ref) => {
  const customCssVars = {
    ...(style || {}),
    ...(bgColor ? { "--sq-fill": bgColor } : {}),
    ...(hoverBgColor ? { "--sq-hover-fill": hoverBgColor } : {}),
    "--sq-radius": `${radius}px`,
  } as React.CSSProperties & Record<string, string>;

  const colorBindingClasses = cn(
    bgColor && "squircle-fill-[var(--sq-fill)]",
    hoverBgColor &&
      "hover:squircle-fill-[var(--sq-hover-fill)] focus:squircle-fill-[var(--sq-hover-fill)]",
  );

  const radiusClasses = cn("squircle-radius-[var(--sq-radius)]");

  return (
    <DropdownMenuPrimitive.RadioItem
      ref={ref}
      className={cn(
        "relative flex cursor-default select-none items-center squircle squircle-radius-[6px] py-1.5 pl-8 pr-2 text-sm outline-none transition-colors hover:squircle-fill-hover-gray focus:squircle-fill-hover-gray data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        colorBindingClasses,
        radiusClasses,
        className,
      )}
      style={customCssVars}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <DotFilledIcon className="h-4 w-4 fill-current" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
});
SqDropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const SqDropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold", inset && "pl-8", className)}
    {...props}
  />
));
SqDropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const SqDropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
));
SqDropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const SqDropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn("ml-auto text-xs tracking-widest opacity-60", className)}
      {...props}
    />
  );
};
SqDropdownMenuShortcut.displayName = "SqDropdownMenuShortcut";

export {
  SqDropdownMenu,
  SqDropdownMenuTrigger,
  SqDropdownMenuContent,
  SqDropdownMenuItem,
  SqDropdownMenuCheckboxItem,
  SqDropdownMenuRadioItem,
  SqDropdownMenuLabel,
  SqDropdownMenuSeparator,
  SqDropdownMenuShortcut,
  SqDropdownMenuGroup,
  SqDropdownMenuPortal,
  SqDropdownMenuSub,
  SqDropdownMenuSubContent,
  SqDropdownMenuSubTrigger,
  SqDropdownMenuRadioGroup,
  SqDropdownItemIndicator,
};
